import React from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { compose, branch, renderComponent } from 'recompose'
import { is, List } from 'immutable'
import { Button, Select } from '@driftt/tide-core-legacy'
import Loading from 'shared-web/src/components/Loading'

import { patchAssistantOrgSettings } from 'app/modules/assistantSettings/actions'
import { patchOrgProperties } from 'app/modules/org/actions'
import {
  salesforceEventSettings,
  salesforceTaskSettings,
  fetchSalesforceSettingsSuccess,
  patchAssistantOrgSettingsPending,
  patchAssistantOrgSettingsSuccess,
  patchAssistantOrgSettingsFailed,
} from 'app/modules/assistantSettings/selectors'

import AssistantConfigOrgSettings from '../AssistantConfigOrgSettings'
import SFDCValues from '../SFDCValues'

class AssistantConfigSalesforceActivity extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      isEditing: false,
      orgSettings: this.props.orgSettings,
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.orgSettings != this.props.orgSettings) {
      this.setState({ orgSettings: nextProps.orgSettings })
    }
  }

  getEventField = () => {
    return this.state.orgSettings
      .get('sfEventFields')
      .keySeq()
      .first()
  }

  handleSalesforceEventObjectChange = ({ value }) => {
    const sfEventField = this.getEventField()
    if (sfEventField) {
      const newSettings = this.state.orgSettings.get('sfEventFields').delete(sfEventField)
      this.setState({
        orgSettings: this.state.orgSettings
          .set('sfEventFields', newSettings)
          .set('salesforceEventObject', value),
      })
    } else {
      this.setState({ orgSettings: this.state.orgSettings.set('salesforceEventObject', value) })
    }
  }

  handleSalesforceEventFieldChange = ({ value }) => {
    const sfEventField = this.getEventField()
    if (sfEventField) {
      const newSettings = this.state.orgSettings.get('sfEventFields').delete(sfEventField)
      this.setState({
        orgSettings: this.state.orgSettings
          .set('sfEventFields', newSettings)
          .setIn(['sfEventFields', value], List.of('')),
      })
    } else {
      this.setState({
        orgSettings: this.state.orgSettings.setIn(['sfEventFields', value], List()),
      })
    }
  }

  removeSalesforceEventField = () => {
    const sfEventField = this.getEventField()
    if (sfEventField) {
      const newSettings = this.state.orgSettings.get('sfEventFields').delete(sfEventField)
      this.setState({ orgSettings: this.state.orgSettings.set('sfEventFields', newSettings) })
    }
  }

  handleSalesforceEventFieldValueChange = (value, index, sfEventField) => {
    this.setState({
      orgSettings: this.state.orgSettings.setIn(['sfEventFields', sfEventField, index], value),
    })
  }

  removeSalesforceEventFieldValue = (index, sfEventField) => {
    const filteredSelectionList = this.state.orgSettings
      .getIn(['sfEventFields', sfEventField])
      .filter(v => v !== '')
    if (filteredSelectionList.size === 1) {
      this.setState({
        orgSettings: this.state.orgSettings
          .deleteIn(['sfEventFields', sfEventField])
          .setIn(['sfEventFields', sfEventField], List.of('')),
      })
    } else {
      this.setState({
        orgSettings: this.state.orgSettings.deleteIn(['sfEventFields', sfEventField, index]),
      })
    }
  }

  getFilteredOrgSettings = () => {
    const sfEventField = this.getEventField()
    if (sfEventField) {
      const filteredSelectionList = this.state.orgSettings
        .getIn(['sfEventFields', sfEventField])
        .filter(v => v !== '')
      return this.state.orgSettings.setIn(['sfEventFields', sfEventField], filteredSelectionList)
    } else {
      return this.state.orgSettings
    }
  }

  isChanged = () => {
    return !is(this.props.orgSettings, this.state.orgSettings)
  }

  handleSave = () => {
    this.props.actions.patchAssistantOrgSettings({
      newSettings: this.getFilteredOrgSettings().toJS(),
      settingName: 'ACTIVITY',
    })
    if (!this.props.hasPassedOnboarding) {
      this.props.actions.patchOrgProperties([
        {
          operation: 'set',
          property: 'hasCustomizedAssistantSFDCActivity',
          value: true,
        },
      ])
    }
  }

  handleCancel = () => {
    this.setState({ orgSettings: this.props.orgSettings })
    if (!this.props.hasPassedOnboarding) {
      this.props.actions.patchOrgProperties([
        {
          operation: 'set',
          property: 'hasCustomizedAssistantSFDCActivity',
          value: true,
        },
      ])
    }
    this.props.handleEdit(false)
  }

  render() {
    const { orgSettings } = this.state
    const {
      salesforceEventSettings,
      salesforceTaskSettings,
      hasPassedOnboarding,
      salesforceOutOfSync,
      patchAssistantOrgSettingsPending,
    } = this.props

    const sfEventObject = orgSettings.get('salesforceEventObject')
    const sfEventField = this.getEventField()
    const sfEventFieldValues = sfEventField ? orgSettings.getIn(['sfEventFields', sfEventField]) : List()
    const filteredSelectionList = sfEventFieldValues.filter(v => v !== '')

    const salesforceEventObjectOptions = {
      multi: false,
      searchable: false,
      autoFocus: false,
      clearable: false,
      placeholder: 'Event object',
      value: sfEventObject,
      options: [{ label: 'Event', value: 'EVENT' }, { label: 'Task', value: 'TASK' }],
      onChange: this.handleSalesforceEventObjectChange,
    }

    const salesforceEventFieldOptions = {
      multi: false,
      autoFocus: false,
      clearable: false,
      placeholder: 'SFDC field...',
      value: sfEventField,
      options:
        sfEventObject === 'EVENT'
          ? salesforceEventSettings.get('eventFields')
          : salesforceTaskSettings.get('taskFields'),
      onChange: this.handleSalesforceEventFieldChange,
    }

    const currentConfigIsValid = !sfEventField || (sfEventField && filteredSelectionList.size > 0)

    const saveEnabled =
      (!hasPassedOnboarding && currentConfigIsValid) ||
      (salesforceOutOfSync && currentConfigIsValid) ||
      (hasPassedOnboarding && this.isChanged() && currentConfigIsValid)

    return (
      <div className="assistant-config-settings-container">
        <h4>What should your team get reminders for?</h4>
        <div className="assistant-settings-container">
          <div className="assistant-settings-subheader">
            <div className="assistant-settings-column">Activity</div>
            <div className="assistant-settings-column">
              Field <span>(optional)</span>
            </div>
            <div className="assistant-settings-column">Value</div>
          </div>
          <div className="assistant-settings-content">
            <div className="assistant-settings-column">
              <Select {...salesforceEventObjectOptions} />
              <span className="micro-copy">with</span>
            </div>
            <div className="assistant-settings-column">
              <Select {...salesforceEventFieldOptions} />
              <span className="micro-copy">is</span>
            </div>
            <SFDCValues
              orgSettings={orgSettings}
              sfObjectType={sfEventObject}
              sfField={sfEventField}
              sfValueSelections={sfEventFieldValues}
              filteredSelections={filteredSelectionList}
              inputHandler={this.handleSalesforceEventFieldValueChange}
              removeHandler={this.removeSalesforceEventFieldValue}
              removeFieldHandler={this.removeSalesforceEventField}
            />
          </div>
        </div>
        <div className="assistant-settings-cta-container">
          <Button
            onClick={this.handleSave}
            disabled={!saveEnabled && !patchAssistantOrgSettingsPending}
            type="primary"
            loading={patchAssistantOrgSettingsPending}
          >
            Save
          </Button>
          <Button onClick={this.handleCancel} type="subtle">
            Cancel
          </Button>
        </div>
      </div>
    )
  }
}

const AssistantConfigSalesforceActivityView = props => {
  const sfEventObject = props.orgSettings.get('salesforceEventObject')
  const sfEventField = props.orgSettings
    .get('sfEventFields')
    .keySeq()
    .first()

  let fieldValues = null
  if (sfEventObject === 'EVENT' && sfEventField) {
    fieldValues = props.salesforceEventSettings.get('eventFieldValues')
  } else if (sfEventField) {
    fieldValues = props.salesforceTaskSettings.get('taskFieldValues')
  }

  const sfFieldLabel = _.get(fieldValues, [sfEventField, 'fieldLabel'], '')

  const salesforceOutOfSync = sfFieldLabel === '' && !!sfEventField

  return (
    <AssistantConfigOrgSettings
      assistantSetting="salesforceActivity"
      sfFieldTitle={sfFieldLabel}
      orgSettings={props.orgSettings}
      handleEdit={props.handleEdit}
      hasPassedOnboarding={props.hasPassedOnboarding}
      salesforceOutOfSync={salesforceOutOfSync}
    />
  )
}

const LoadingState = () => {
  return (
    <div className="SFDC-config-loading-container">
      <Loading color="#CCCCCC" />
    </div>
  )
}

const mapStateToProps = state => ({
  salesforceEventSettings: salesforceEventSettings(state),
  salesforceTaskSettings: salesforceTaskSettings(state),
  fetchSalesforceSettingsSuccess: fetchSalesforceSettingsSuccess(state),
  patchAssistantOrgSettingsPending: patchAssistantOrgSettingsPending(state),
  patchAssistantOrgSettingsSuccess: patchAssistantOrgSettingsSuccess(state),
  patchAssistantOrgSettingsFailed: patchAssistantOrgSettingsFailed(state),
})

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      patchAssistantOrgSettings,
      patchOrgProperties,
    },
    dispatch
  ),
})

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  branch(props => !props.orgSettings || !props.fetchSalesforceSettingsSuccess, renderComponent(LoadingState)),
  branch(props => !props.isEditing, renderComponent(AssistantConfigSalesforceActivityView))
)(AssistantConfigSalesforceActivity)



// WEBPACK FOOTER //
// ./src/scripts/components/settings/AssistantConfigOrg/AssistantConfigSalesforceActivity/index.js